import { Fragment, useEffect, useMemo, useState } from 'react';

import CachedIcon from '@mui/icons-material/Cached';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import FilePondPluginGetFile from 'filepond-plugin-get-file';
import 'filepond-plugin-get-file/dist/filepond-plugin-get-file.min.css';

import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css';

import FilePondPluginMediaPreview from 'filepond-plugin-media-preview';
import FilePondPluginPdfPreview from 'filepond-plugin-pdf-preview';

import 'filepond/dist/filepond.min.css';
import { FilePond, registerPlugin } from 'react-filepond';

import { getActiveDiakok } from '../../../api/masterdataAPI.js';
import { backendHOST } from '../../../client-config/client-config';

import './NewHomework.scss';

registerPlugin(
  FilePondPluginMediaPreview,
  FilePondPluginPdfPreview,
  FilePondPluginGetFile,
  FilePondPluginImagePreview,
);

const url = backendHOST + '/api/upload/homework';

const DEFAULT_TITLE = 'Házi feladat!';
const DEFAULT_DESCRIPTION =
  'A mellékelt feladatok megoldásait töltsd fel vagy küld el nekünk a következő óráig! Köszönjük!';

export default function NewHomework() {
  const [files, setFiles] = useState([]);
  const [descr, setDescr] = useState(DEFAULT_DESCRIPTION);
  const [targy, setTitle] = useState(DEFAULT_TITLE);
  const [selectedDiak, setSelectedDiak] = useState(null);
  const [diakok, setDiakok] = useState([]);
  const [loadingDiakok, setLoadingDiakok] = useState(false);

  const refreshHomework = () => {
    setFiles([]);
    setSelectedDiak(null);
    setTitle(DEFAULT_TITLE);
    setDescr(DEFAULT_DESCRIPTION);
  };

  useEffect(() => {
    async function apiCall() {
      setLoadingDiakok(true);

      try {
        const apiResponse = await getActiveDiakok();
        setDiakok(Array.isArray(apiResponse) ? apiResponse : []);
      } catch (error) {
        console.error('Aktív diákok betöltési hiba:', error);
        setDiakok([]);
      } finally {
        setLoadingDiakok(false);
      }
    }

    apiCall();
  }, []);

  const canUpload = Boolean(selectedDiak?.id && targy.trim() && descr.trim());

  const serverSettings = useMemo(
    () => ({
      url,
      process: {
        headers: {
          'x-auth-token': localStorage.token,
        },
        onload: (response) => {
          try {
            const parsed = JSON.parse(response);
            return parsed.key ?? response;
          } catch {
            return response;
          }
        },
        onerror: (error) => {
          console.error('Házi feltöltési hiba:', error);
          return error?.message ?? 'Feltöltési hiba';
        },
        ondata: (formData) => {
          formData.append('homeWorkTitle', targy);
          formData.append('homeWorkDescr', descr);
          formData.append('diak_id', selectedDiak.id);
          formData.append('created_at', new Date().toISOString());

          return formData;
        },
      },
    }),
    [targy, descr, selectedDiak],
  );

  return (
    <Fragment>
      <Card variant='outlined' sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              spacing={2}
            >
              <Box>
                <Typography variant='subtitle1' fontWeight={700}>
                  Új házi feladat rögzítése
                </Typography>

                <Typography variant='body2' color='text.secondary'>
                  Válassz diákot, add meg a címet és tölts fel maximum 5 fájlt.
                </Typography>
              </Box>

              <Tooltip title='Űrlap alaphelyzetbe állítása'>
                <IconButton size='large' onClick={refreshHomework}>
                  <CachedIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            <Autocomplete
              id='diak-selector'
              options={Array.isArray(diakok) ? diakok : []}
              loading={loadingDiakok}
              clearOnEscape
              value={selectedDiak}
              onChange={(event, newValue) => {
                setSelectedDiak(newValue);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option?.text2 ?? option?.text ?? ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Diák kiválasztása'
                  size='small'
                  fullWidth
                />
              )}
            />

            <TextField
              label='Házi feladat címe'
              value={targy}
              onChange={(e) => setTitle(e.target.value)}
              size='small'
              fullWidth
            />

            <TextField
              label='Leírás / üzenet a diáknak'
              value={descr}
              onChange={(e) => setDescr(e.target.value)}
              multiline
              minRows={4}
              fullWidth
            />

            {selectedDiak ? (
              <Alert severity='info' variant='outlined'>
                Házi feladat címzettje: <strong>{selectedDiak.text}</strong>
              </Alert>
            ) : (
              <Alert severity='warning' variant='outlined'>
                Feltöltés előtt válassz diákot.
              </Alert>
            )}

            <Box
              sx={{
                opacity: canUpload ? 1 : 0.6,
              }}
            >
              <FilePond
                files={files}
                onupdatefiles={setFiles}
                allowMultiple
                allowDownloadByUrl={false}
                allowImagePreview
                filePosterHeight={160}
                disabled={!canUpload}
                styleButtonRemoveItemPosition='right'
                maxFiles={5}
                server={serverSettings}
                name='files'
                labelIdle='Húzd ide a fájlokat vagy <span class="filepond--label-action">válaszd ki</span> a feltöltéshez!'
              />
            </Box>

            <Button
              variant='outlined'
              onClick={refreshHomework}
              startIcon={<CachedIcon />}
            >
              Új házi feladat kezdése
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Fragment>
  );
}
